import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Car,
  Users,
  Package,
  DollarSign,
  Clock,
  Eye,
  Activity,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { useVehicleCategories, useDeleteVehicleCategory, useToggleVehicleCategoryStatus } from '@/api-services/hooks/useVehicleCategory';
import { VehicleCategory, StatusOption } from '@/api-services/hooks/vehicleCategoryTypes';
import DeleteVehicleCategoryConfirmation from '@/components/vehicle-categories/DeleteVehicleCategoryConfirmation';

const VehicleCategoriesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusOption>('all');

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [vehicleCategoryToDelete, setVehicleCategoryToDelete] = useState<{ id: number; details: string } | null>(null);

  
  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Determine if we need client-side filtering
  const hasClientSideFilters = statusFilter !== 'all';
  
  // When client-side filters are active, fetch a large page size to have enough data to filter
  // Otherwise, use normal pagination
  const apiPageSize = hasClientSideFilters ? 1000 : itemsPerPage;
  const apiPage = hasClientSideFilters ? 0 : currentPage; // Always fetch from page 0 when filtering client-side

  // Reset to first page when search filter changes (client-side filters don't reset page)
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm]);

  // Fetch vehicle categories from API (only search filter goes to API, status filter is client-side)
  // Only send search to API if search term is more than 3 characters
  const shouldSearch = debouncedSearchTerm.trim().length > 2;
  const { data, isLoading: loading, error, refetch } = useVehicleCategories(
    {
      page: apiPage,
      size: apiPageSize,
      ...(shouldSearch && { search: debouncedSearchTerm.trim() }),
    },
    {
      enabled: true, // Always enabled, but search param only included if > 3 chars
      onError: (err) => {
        toast.error(`Failed to load vehicle categories: ${err.message}`);
      },
    }
  );

  // Delete vehicle category hook
  const { mutate: deleteVehicleCategory, isPending: isDeleting } = useDeleteVehicleCategory({
    onSuccess: async (data, categoryId) => {
      toast.success('Vehicle category deleted successfully!');
      // Invalidate all vehicle categories queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['vehicle-categories'] });
    },
    onError: (error, categoryId) => {
      toast.error(`Failed to delete vehicle category: ${error.message}`);
    },
  });

  // Toggle vehicle category status hook
  const { mutate: toggleStatus, isPending: isToggling } = useToggleVehicleCategoryStatus({
    onSuccess: async (data, categoryId) => {
      toast.success('Vehicle category status updated successfully!');
      // Update only the changed row in the cache instead of refetching entire table
      queryClient.setQueriesData(
        { queryKey: ['vehicle-categories'] },
        (oldData: any) => {
          if (!oldData?.content) return oldData;

          const updatedContent = oldData.content.map((category: any) =>
            category.vehicleCategoryId === categoryId
              ? { ...category, isActive: !category.isActive }
              : category
          );

          return {
            ...oldData,
            content: updatedContent,
          };
        }
      );
    },
    onError: (error, categoryId) => {
      toast.error(`Failed to update vehicle category status: ${error.message}`);
    },
  });

  const getStatusBadge = useCallback((isActive: boolean) => {
    return isActive
      ? { variant: 'default', className: 'bg-success text-success-foreground', label: 'Active' }
      : { variant: 'secondary', className: 'bg-muted text-muted-foreground', label: 'Inactive' };
  }, []);

  const handleVehicleCategoryAction = (action: string, categoryId: number, categoryDetails: string) => {
    if (action === 'Edit') {
      navigate(`/admin/vehicle-categories/edit/${categoryId}`);
    } else if (action === 'View') {
      navigate(`/admin/vehicle-categories/view/${categoryId}`);
    } else if (action === 'Toggle Status') {
      toggleStatus(categoryId);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (categoryId: number, categoryDetails: string) => {
    setVehicleCategoryToDelete({ id: categoryId, details: categoryDetails });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = (categoryId: number) => {
    deleteVehicleCategory(categoryId);
    setDeleteConfirmationOpen(false);
    setVehicleCategoryToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setVehicleCategoryToDelete(null);
  };

  // Filter vehicle categories by status on the client side
  const vehicleCategories = useMemo(() => {
    const categoryData = data as any;
    if (!categoryData || !categoryData.content) return [];

    let filtered = categoryData.content;

    // Filter by status (active/inactive)
    if (statusFilter !== 'all') {
      filtered = filtered.filter((category: VehicleCategory) => {
        if (statusFilter === 'active') {
          return category.isActive === true;
        } else if (statusFilter === 'inactive') {
          return category.isActive === false;
        }
        return true;
      });
    }

    return filtered;
  }, [data, statusFilter, hasClientSideFilters]);

  // Pagination calculations - use filtered count for client-side filters
  const totalFilteredItems = vehicleCategories.length;
  const categoryData = data as any;
  const totalItems = categoryData?.page?.totalElements ?? 0;
  
  // Calculate total pages based on filtered items
  const totalPages = useMemo(() => {
    if (hasClientSideFilters) {
      // Client-side filtering: paginate filtered results
      return totalFilteredItems > 0 ? Math.ceil(totalFilteredItems / itemsPerPage) : 0;
    }
    // Server-side pagination: use API response
    const pages = categoryData?.page?.totalPages ?? 0;
    // If totalPages is 0 but we have totalElements, calculate it
    if (pages === 0 && categoryData?.page?.totalElements && categoryData?.page?.totalElements > 0) {
      return Math.ceil(categoryData?.page?.totalElements / itemsPerPage);
    }
    // If totalPages is still 0 but we have content, calculate from content length
    if (pages === 0 && categoryData?.content && categoryData.content.length > 0) {
      return Math.ceil(categoryData.content.length / itemsPerPage);
    }
    return pages;
  }, [totalFilteredItems, itemsPerPage, hasClientSideFilters, categoryData]);

  // Paginated vehicle categories for current page (when using client-side filtering)
  const paginatedVehicleCategories = useMemo(() => {
    if (!hasClientSideFilters) {
      // No client-side filtering, use API pagination directly
      return categoryData?.content ?? [];
    }
    // Client-side pagination for filtered results
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return vehicleCategories.slice(startIndex, endIndex);
  }, [vehicleCategories, currentPage, itemsPerPage, hasClientSideFilters, categoryData]);

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Check if current page is empty after data loads and navigate to previous page if needed
  useEffect(() => {
    if (!loading && categoryData && paginatedVehicleCategories.length === 0 && currentPage > 0 && totalPages > 0) {
      // Current page is empty, navigate to the last valid page
      const lastPage = Math.max(0, totalPages - 1);
      setCurrentPage(lastPage);
    }
  }, [loading, paginatedVehicleCategories.length, currentPage, totalPages, categoryData]);

  // Format vehicle category details for display - wrapped in useCallback to prevent infinite re-renders
  const getVehicleCategoryDetails = useCallback((category: VehicleCategory) => {
    return `${category.vehicleCategoryName || 'Unknown Category'}`;
  }, []);

  const getCurrencyDisplay = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading vehicle categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <TrendingDown className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error Loading Vehicle Categories</h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Vehicle Categories</h1>
          <p className="text-muted-foreground mt-1">Manage vehicle types, pricing, and capacity settings</p>
        </div>
        <Button
          className="bg-gradient-primary hover:opacity-90"
          onClick={() => navigate('/admin/vehicle-categories/add')}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Vehicle Category
        </Button>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search vehicle categories by name... (min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  {statusFilter === 'all' ? 'All Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setStatusFilter('all')}>All Status</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('active')}>Active</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('inactive')}>Inactive</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Categories Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="w-5 h-5" />
            Vehicle Categories (
            {loading 
              ? "..." 
              : hasClientSideFilters 
                ? `${totalFilteredItems} filtered` 
                : totalItems > 0 ? totalItems : (categoryData?.content?.length ?? 0)}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Capacity</TableHead>
                  <TableHead>Luggage</TableHead>
                  <TableHead>Base Fare</TableHead>
                  <TableHead>Rate per KM</TableHead>
                  <TableHead>Wait Time</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedVehicleCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      {vehicleCategories.length === 0
                        ? "No vehicle categories found"
                        : "No vehicle categories match the current filters"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedVehicleCategories.map((category) => {
                    const statusBadge = getStatusBadge(category.isActive);
                    const categoryDetails = getVehicleCategoryDetails(category);

                    return (
                      <TableRow key={category.vehicleCategoryId} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-blue-100 text-blue-800">
                                <Car className="h-5 w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">{category.vehicleCategoryName || 'Unknown Category'}</div>
                              <div className="text-sm text-muted-foreground">ID: {category.vehicleCategoryId}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate">
                            {category.vehicleDescription || 'No description'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">{category.maxPassengerCount}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Package className="w-4 h-4 text-orange-500" />
                            <span className="font-medium">{category.maxLuggageVolume}L</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-semibold text-green-600">
                            {getCurrencyDisplay(category.baseFare)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-blue-600">
                            {getCurrencyDisplay(category.ratePerKm)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-purple-500" />
                            <span>{category.waitTimeLimit} min</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {category.isActive ? (
                              <>
                                <ToggleRight className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() => {
                                  navigate(`/admin/vehicle-categories/view/${category.vehicleCategoryId || 0}`);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => handleVehicleCategoryAction('Edit', category.vehicleCategoryId || 0, categoryDetails)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Category
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => handleVehicleCategoryAction('Toggle Status', category.vehicleCategoryId || 0, categoryDetails)}
                                disabled={isToggling}
                              >
                                {category.isActive ? (
                                  <>
                                    <ToggleLeft className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <ToggleRight className="w-4 h-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => handleDeleteClick(category.vehicleCategoryId || 0, categoryDetails)}
                                className="text-destructive"
                                disabled={isDeleting}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Category
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination - Always show if totalPages > 0, even if current page is empty */}
            {totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={hasClientSideFilters ? totalFilteredItems : totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Vehicle Category Confirmation Modal */}
      <DeleteVehicleCategoryConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        vehicleCategoryDetails={vehicleCategoryToDelete?.details || ''}
        vehicleCategoryId={vehicleCategoryToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default VehicleCategoriesPage;